import { Category } from '~/models/Category.models';
import type { OrderReceiptItemModel } from '~/models/order/OrderReceiptItem.api.model';
import type { ShoppingCartItemModel } from '~/models/shopping_cart/ShoppingCartItem.api.model';

type EcommerceItem = {
  item_brand?: string;
  item_category?: string;
  item_id?: number;
  item_name?: string;
  item_variant?: string;
  down_pay_time?: number;
  price?: number;
  quantity?: number;
};

export const mapOrderReceiptItemModelToEcommerceItem = (
  item: OrderReceiptItemModel,
): EcommerceItem[] => {
  if (item.BundlePlanItem && !!item.BundlePlanItem.Id) {
    return [
      {
        down_pay_time: item.SelectedPayment?.DownPayments,
        item_brand: item.ManufacturerName,
        item_category: item.Category,
        item_id: item.Id,
        item_name: item.ProductName,
        item_variant: `${item.StorageSize?.DataSize || ''}${item.StorageSize?.DataUnit ? item.StorageSize?.DataUnit + ' ' : ''}${item.ColorName || ''}`,
        price:
          item.PaymentOverDownPayTimePeriod ||
          item.SelectedPayment?.ProductDownPayment,
        quantity: 1,
      },
      {
        item_brand: 'Halebop',
        item_category: 'Abonnemang',
        item_id: item.BundlePlanItem.Id, //PlanId
        item_name: item.BundlePlanItem.ProductName,
        item_variant:
          item.BundlePlanItem.BindingPeriod === 1 ? '0 månader' : '24 månader',
        price: item.BundlePlanItem.MonthlyPrice,
        quantity: 1,
      },
    ];
  } else if (item.Category === Category.prePaid) {
    return [
      {
        item_brand: 'Halebop',
        item_category: item.Category,
        item_id: item.Id ?? 0, //PlanId?
        item_name: item.ProductName,
        item_variant: item.BindingPeriod + ' månader',
        price: item.OneTimePrice,
        quantity: 1,
      },
    ];
  } else if (
    item.Category === Category.postPaid ||
    item.Category === Category.family
  ) {
    return [
      {
        item_brand: 'Halebop',
        item_category: item.Category,
        item_id: item.Id ?? 0, //PlanId?
        item_name: item.ProductName,
        item_variant: item.BindingPeriod + ' månader',
        price: item.MonthlyPrice,
        quantity: 1,
      },
    ];
  } else if (
    item.BundlePlanItem?.FamilyPlanChildPlanItems &&
    item.BundlePlanItem?.FamilyPlanChildPlanItems.length > 0
  ) {
    return item.BundlePlanItem?.FamilyPlanChildPlanItems.flatMap(
      (familyPlanItem) => [
        {
          item_brand: 'Halebop',
          item_category: familyPlanItem.Category,
          item_id: familyPlanItem.Id ?? 0, //PlanId?
          item_name: familyPlanItem.ProductName,
          item_variant: familyPlanItem.BindingPeriod + ' månader',
          price: familyPlanItem.MonthlyPrice,
          quantity: 1,
        },
      ],
    );
  } else if (item.Category === Category.insurances) {
    return [
      {
        item_brand: 'Halebop',
        item_category: item.Category,
        item_id: item.Id, //ProductAttributeCombinationId ?
        item_name: 'Mobilförsäkring',
        item_variant: item.ProductName,
        price: item.MonthlyPrice,
        quantity: 1,
      },
    ];
  } else {
    return [
      {
        item_brand: item.ManufacturerName,
        item_category: item.Category,
        item_id: item.Id, //ProductAttributeCombinationId ?
        item_name: item.ProductName,
        item_variant: `${item.StorageSize?.DataSize ? item.StorageSize?.DataSize : ''}${item.StorageSize?.DataUnit ? item.StorageSize?.DataUnit + ' ' : ''}${item.ColorName ?? ''}`,
        price:
          item.PaymentOverDownPayTimePeriod ||
          item.SelectedPayment?.ProductDownPayment,
        quantity: 1,
      },
    ];
  }
};

export const mapCartItemModelToEcommerceItem = (
  item: ShoppingCartItemModel,
  selectedDownPayTime?: number,
): EcommerceItem[] => {
  if (item.BundlePlanItem != null) {
    return [
      {
        down_pay_time: selectedDownPayTime,
        item_brand: item.ManufacturerName,
        item_category: item.Category,
        item_id: item.ProductAttributeCombinationId,
        item_name: item.ProductName,
        item_variant: `${item.StorageSize?.DataSize ? item.StorageSize?.DataSize : ''}${item.StorageSize?.DataUnit ? item.StorageSize?.DataUnit + ' ' : ''}${item.Color ?? ''}`,
        price: item.Payments?.find((payment) => payment.DownPayments === 1)
          ?.ProductDownPayment,
        quantity: 1,
      },
      {
        item_brand: 'Halebop',
        item_category: 'Abonnemang',
        item_id: item.BundlePlanItem.PlanId,
        item_name: item.BundlePlanItem.ProductName,
        item_variant:
          item.BundlePlanItem.BindingPeriod === 1 ? '0 månader' : '24 månader',
        price: item.BundlePlanItem.MonthlyPrice,
        quantity: 1,
      },
    ];
  } else if (item.Category === Category.prePaid) {
    return [
      {
        item_brand: 'Halebop',
        item_category: item.Category,
        item_id: item.PlanId ?? 0,
        item_name: item.ProductName,
        item_variant: item.BindingPeriod + ' månader',
        price: item.OneTimePrice,
        quantity: 1,
      },
    ];
  } else if (
    item.Category === Category.postPaid ||
    item.Category === Category.family
  ) {
    return [
      {
        item_brand: 'Halebop',
        item_category: item.Category,
        item_id: item.PlanId ?? 0,
        item_name: item.ProductName,
        item_variant: item.BindingPeriod + ' månader',
        price: item.MonthlyPrice,
        quantity: 1,
      },
    ];
  } else if (item.Category === Category.insurances) {
    return [
      {
        item_brand: 'Halebop',
        item_category: item.Category,
        item_id: item.ProductAttributeCombinationId,
        item_name: 'Mobilförsäkring',
        item_variant: item.ProductName,
        price: item.MonthlyPrice,
        quantity: 1,
      },
    ];
  } else {
    return [
      {
        down_pay_time: selectedDownPayTime,
        item_brand: item.ManufacturerName,
        item_category: item.Category,
        item_id: item.ProductAttributeCombinationId,
        item_name: item.ProductName,
        item_variant: `${item.StorageSize?.DataSize ? item.StorageSize?.DataSize : ''}${item.StorageSize?.DataUnit ? item.StorageSize?.DataUnit + ' ' : ''}${item.Color ?? ''}`,
        price: item.Payments?.find((payment) => payment.DownPayments === 1)
          ?.ProductDownPayment,
        quantity: 1,
      },
    ];
  }
};
